/**
 * Personal sandboxes are scaled to zero every night. The frontend and Cognito
 * keep working, so sign-in succeeds and the first API call then fails. See
 * packages/encamp-cli/src/commands/sandbox/README.md.
 */

/**
 * Never shut down, so a failed API call there is a real outage. Same list and
 * exact matching as the CLI's sandbox guard, so a sandbox named `devon` is
 * still a sandbox.
 */
const SHARED_ENVIRONMENTS: ReadonlySet<string> = new Set(["dev", "demo", "release", "release_ro"]);

/** The status codes API Gateway or the ALB answer with when encamp-api has no running tasks. */
const NO_BACKEND_STATUS_CODES: ReadonlySet<number> = new Set([502, 503, 504]);

const LOCAL_API_HOSTS: ReadonlySet<string> = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "[::1]",
]);

export function isPersonalSandboxEnv(env: string | undefined): env is string {
  const normalized = env?.trim().toLowerCase() ?? "";
  return normalized.length > 0 && !SHARED_ENVIRONMENTS.has(normalized);
}

/**
 * True when the API is the deployed sandbox's own API. A local API
 * (`yarn encamp local`, or no VITE_API_URL, which falls back to localhost:4000)
 * being down is the developer's server not running, not a shutdown.
 */
export function isDeployedApiUrl(apiUrl: string | undefined): boolean {
  if (!apiUrl?.trim()) return false;
  try {
    return !LOCAL_API_HOSTS.has(new URL(apiUrl.trim()).hostname);
  } catch {
    return false;
  }
}

function field(value: unknown, key: string): unknown {
  return typeof value === "object" && value !== null ? Reflect.get(value, key) : undefined;
}

/**
 * `fetch`'s own rejection when the browser got no response (Chrome, Safari,
 * Firefox). Matched by message too, because a bug in the app's own code
 * (`Cannot read properties of undefined`) is also a TypeError.
 */
const FETCH_FAILURE_MESSAGES =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/;

/** Other status-less errors (a failed token refresh) are not this. */
function isFetchFailure(networkError: unknown): boolean {
  return (
    networkError instanceof Error &&
    networkError.name === "TypeError" &&
    FETCH_FAILURE_MESSAGES.test(networkError.message)
  );
}

/**
 * A fetch failure or a 502/503/504: how a scaled-down API looks to the browser,
 * depending on whether the error response carries CORS headers. Takes an
 * ApolloError or a bare network error.
 */
export function isApiUnreachableError(error: unknown): boolean {
  const networkError = field(error, "networkError") ?? error;
  if (!networkError) return false;

  const statusCode = field(networkError, "statusCode");
  if (typeof statusCode === "number") {
    return NO_BACKEND_STATUS_CODES.has(statusCode);
  }
  return isFetchFailure(networkError);
}

export type SandboxOffDetection = { isSandboxOff: true; env: string } | { isSandboxOff: false };

const NOT_OFF: SandboxOffDetection = { isSandboxOff: false };

/**
 * Whether the error looks like a turned-off sandbox, and which. Only a personal sandbox
 * talking to its deployed API qualifies. VITE_ENV and VITE_API_URL are what
 * `yarn encamp env` writes from terraform.
 */
export function detectSandboxOff(
  error: unknown,
  {
    env = import.meta.env.VITE_ENV,
    apiUrl = import.meta.env.VITE_API_URL,
  }: { env?: string; apiUrl?: string } = {}
): SandboxOffDetection {
  if (!isPersonalSandboxEnv(env) || !isDeployedApiUrl(apiUrl)) return NOT_OFF;
  if (!isApiUnreachableError(error)) return NOT_OFF;
  return { isSandboxOff: true, env: env.trim() };
}
